import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { TranscriptionConsentStatus } from '../model/useTranscriptionConsent'

export type TranscriptionConsentPanelProps = {
  status: TranscriptionConsentStatus
  /** In-person consultation: no patient app, so only the verbal answer is offered. */
  inPerson: boolean
  busy: boolean
  onRequestFromPatient: () => Promise<void>
  onVerbalAnswer: (accepted: boolean) => Promise<void>
}

/**
 * Shown instead of the start button until the patient accepts: ask on the patient's screen
 * (teleconsultation) or record the answer the patient gave verbally.
 */
export function TranscriptionConsentPanel({ status, inPerson, busy, onRequestFromPatient, onVerbalAnswer }: TranscriptionConsentPanelProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<'requestError' | 'recordError' | null>(null)
  const key = (suffix: string) => `page.liveConsultation.transcription.consent.${suffix}`

  // Promise.resolve().then(): a synchronous throw must land in the same catch, never escape the click handler.
  const run = (action: () => Promise<void>, errorKey: 'requestError' | 'recordError') => {
    setError(null)
    Promise.resolve()
      .then(action)
      .catch(() => setError(errorKey))
  }

  return (
    <Alert severity={status === 'refused' ? 'warning' : 'info'} data-testid="transcription-consent-panel">
      <AlertTitle>{t(key('title'))}</AlertTitle>
      <Typography variant="body2">{t(key(messageKey(status, inPerson)))}</Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
        {!inPerson && status !== 'waiting' && (
          <Button size="small" variant="contained" disabled={busy} onClick={() => run(onRequestFromPatient, 'requestError')} data-testid="consent-ask-patient">
            {t(key(status === 'refused' ? 'askAgain' : 'askPatient'))}
          </Button>
        )}
        {status === 'waiting' && <CircularProgress size={16} />}
        <Button size="small" variant={inPerson ? 'contained' : 'outlined'} disabled={busy} onClick={() => run(() => onVerbalAnswer(true), 'recordError')} data-testid="consent-verbal-accepted">
          {t(key('verbalAccepted'))}
        </Button>
        <Button size="small" color="inherit" disabled={busy} onClick={() => run(() => onVerbalAnswer(false), 'recordError')} data-testid="consent-verbal-refused">
          {t(key('verbalRefused'))}
        </Button>
      </Stack>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {t(key(error))}
        </Typography>
      )}
    </Alert>
  )
}

/** In person there is no "ask the patient" button, so the refusal message must not suggest asking again. */
function messageKey(status: TranscriptionConsentStatus, inPerson: boolean): string {
  if (status === 'waiting') return 'waiting'
  if (status === 'refused') return inPerson ? 'refusedInPerson' : 'refused'
  return 'explanation'
}

export default TranscriptionConsentPanel
